import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

/**
 * Adds depth fading capability to a LineMaterial.
 * This injects custom shader code that samples a depth texture and fades the line
 * when it's behind the point cloud.
 */
export function enableDepthFadeOnLineMaterial(material: LineMaterial): void {
  // Skip if already set up
  const mat = material as LineMaterial & { _depthFadeSetup?: boolean };
  if (mat._depthFadeSetup) {
    return;
  }
  mat._depthFadeSetup = true;

  // Add custom uniforms for depth fading
  material.uniforms.uDepthTexture = { value: null };
  material.uniforms.uNear = { value: 0.1 };
  material.uniforms.uFar = { value: 10000 };
  material.uniforms.uFadeDistance = { value: 2.0 }; // Fade distance in world units
  material.uniforms.uFadeStart = { value: 1.0 }; // Start fading when 1 unit behind
  material.uniforms.uDepthFadeEnabled = { value: false };
  material.uniforms.uResolution = { value: new THREE.Vector2(1, 1) };

  const originalOnBeforeCompile = material.onBeforeCompile;

  material.onBeforeCompile = (shader, renderer) => {
    // Call original if it exists
    if (originalOnBeforeCompile) {
      originalOnBeforeCompile.call(material, shader, renderer);
    }

    // Add our uniforms to the shader
    shader.uniforms.uDepthTexture = material.uniforms.uDepthTexture;
    shader.uniforms.uNear = material.uniforms.uNear;
    shader.uniforms.uFar = material.uniforms.uFar;
    shader.uniforms.uFadeDistance = material.uniforms.uFadeDistance;
    shader.uniforms.uFadeStart = material.uniforms.uFadeStart;
    shader.uniforms.uDepthFadeEnabled = material.uniforms.uDepthFadeEnabled;
    shader.uniforms.uResolution = material.uniforms.uResolution;

    // Add uniforms at the top of fragment shader (before void main)
    // Include uResolution since LineMaterial's resolution might not be in scope
    const depthFadeUniforms = `
uniform sampler2D uDepthTexture;
uniform float uNear;
uniform float uFar;
uniform float uFadeDistance;
uniform float uFadeStart;
uniform bool uDepthFadeEnabled;
uniform vec2 uResolution;
`;

    // Insert uniforms after linewidth uniform
    shader.fragmentShader = shader.fragmentShader.replace('uniform float linewidth;', 'uniform float linewidth;\n' + depthFadeUniforms);

    // Add the helper functions and apply depth fade inside main(), right before gl_FragColor
    // The LineMaterial shader has: gl_FragColor = vec4( diffuseColor.rgb, alpha );
    const originalLine = 'gl_FragColor = vec4( diffuseColor.rgb, alpha );';
    const replacementCode = `{
      float fadedAlpha = alpha;

      if (uDepthFadeEnabled) {
        vec2 screenUV = gl_FragCoord.xy / uResolution;
        float pcDepth = texture2D(uDepthTexture, screenUV).r;

        // Only apply fade if there's valid point cloud depth
        if (pcDepth > 0.0 && pcDepth < 1.0) {
          // Linearize depths for proper distance comparison
          float zNdc = pcDepth * 2.0 - 1.0;
          float pcLinearDepth = (2.0 * uNear * uFar) / (uFar + uNear - zNdc * (uFar - uNear));

          float fragNdc = gl_FragCoord.z * 2.0 - 1.0;
          float fragLinearDepth = (2.0 * uNear * uFar) / (uFar + uNear - fragNdc * (uFar - uNear));

          // How far behind the point cloud is this fragment?
          // Positive = behind point cloud, negative = in front
          float depthDiff = fragLinearDepth - pcLinearDepth;

          // Fade based on depthDiff:
          // - In front or at surface (depthDiff <= 0): full alpha
          // - Behind: fade from 1 to 0 as depthDiff goes from uFadeStart to uFadeStart + uFadeDistance
          if (depthDiff > uFadeStart) {
            float fadeAmount = clamp(depthDiff / uFadeDistance, 0.0, 1.0);
            fadedAlpha = alpha * (1.0-fadeAmount);
          }
        }
      }

      if (fadedAlpha < 0.01) discard;
      gl_FragColor = vec4(diffuseColor.rgb, fadedAlpha);
    }`;

    if (shader.fragmentShader.includes(originalLine)) {
      shader.fragmentShader = shader.fragmentShader.replace(originalLine, replacementCode);
    } else {
      console.warn('DepthFadeUtils: Could not find gl_FragColor line to replace in LineMaterial shader');
    }
  };

  // Enable transparency and alpha blending
  material.transparent = true;
  material.depthWrite = false; // Don't write to depth buffer for transparent objects
  material.blending = THREE.NormalBlending;
  material.blendSrc = THREE.SrcAlphaFactor;
  material.blendDst = THREE.OneMinusSrcAlphaFactor;
  material.blendEquation = THREE.AddEquation;

  // Force shader recompilation
  material.needsUpdate = true;
}

/**
 * Adds depth fading capability to a MeshBasicMaterial by replacing it with a ShaderMaterial.
 * Returns a new ShaderMaterial that should replace the original.
 */
export function createDepthFadeMeshMaterial(color: THREE.Color, opacity = 1.0, side: THREE.Side = THREE.FrontSide): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: color },
      uOpacity: { value: opacity },
      uDepthTexture: { value: null },
      uNear: { value: 0.1 },
      uFar: { value: 10000 },
      uFadeDistance: { value: 2.0 },
      uFadeStart: { value: 1.0 },
      uDepthFadeEnabled: { value: false },
      uResolution: { value: new THREE.Vector2(1, 1) },
    },
    vertexShader: `
      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform sampler2D uDepthTexture;
      uniform float uNear;
      uniform float uFar;
      uniform float uFadeDistance;
      uniform float uFadeStart;
      uniform bool uDepthFadeEnabled;
      uniform vec2 uResolution;

      void main() {
        float fadedAlpha = uOpacity;

        if (uDepthFadeEnabled) {
          vec2 screenUV = gl_FragCoord.xy / uResolution;
          float pcDepth = texture2D(uDepthTexture, screenUV).r;

          // Only apply fade if there's valid point cloud depth
          if (pcDepth > 0.0 && pcDepth < 1.0) {
            // Linearize depths for proper distance comparison
            float zNdc = pcDepth * 2.0 - 1.0;
            float pcLinearDepth = (2.0 * uNear * uFar) / (uFar + uNear - zNdc * (uFar - uNear));

            float fragNdc = gl_FragCoord.z * 2.0 - 1.0;
            float fragLinearDepth = (2.0 * uNear * uFar) / (uFar + uNear - fragNdc * (uFar - uNear));

            // How far behind the point cloud is this fragment?
            float depthDiff = fragLinearDepth - pcLinearDepth;

            // Fade based on depthDiff:
            // - In front or at surface (depthDiff <= uFadeStart): full alpha
            // - Behind: fade from 1 to 0 over uFadeDistance
            if (depthDiff > uFadeStart) {
              float fadeAmount = clamp((depthDiff - uFadeStart) / uFadeDistance, 0.0, 1.0);
              fadedAlpha = uOpacity * (1.0 - fadeAmount);
            }
          }
        }

        if (fadedAlpha < 0.01) discard;
        gl_FragColor = vec4(uColor, fadedAlpha);
      }
    `,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    side: side,
  });
}
